import React from 'react';
import * as Yup from 'yup';
import isPayingSeller from '@repo/checkout/lib/isPayingSeller';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import { Form } from '@dashboard/components/Formik';
import Features from '@dashboard/enums/features';

const validationSchema = Yup.object().shape({
  displayName: Yup.string().required('Store name is required'),
  brandColor: Yup.string(),
  defaultCurrency: Yup.string().required('Default currency is required'),
  url: Yup.string().url('Must be a valid URL'),
});

const StoreForm = () => {
  const { seller } = useAuth();

  const { updateSeller } = useSellerQueries();

  // Only paying sellers (active/legacy) can remove our branding. Trialing sellers
  // are locked too: the API forces the flag back on for them (SellerController),
  // matching the per-page badge lock in the theme editor.
  const canRemoveBranding = isPayingSeller(seller);

  return (
    <Form
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={{
        displayName: seller.displayName,
        brandColor: seller.brandColor,
        logo: seller.logo,
        defaultCurrency: seller.defaultCurrency,
        favicon: seller.favicon,
        showCheckoutPageBranding: canRemoveBranding
          ? seller.showCheckoutPageBranding !== false
          : true,
      }}
      fields={() => [
        {
          name: 'displayName',
          required: true,
          type: 'text',
          label: 'Store name',
        },
        {
          name: 'logo',
          required: false,
          type: 'image',
          label: 'Logo',
          image: seller.logo,
          crop: { height: 512, width: 512, aspect: 1 },
          previewSize: 'logo',
          onUpdate: async image => {
            await updateSeller({ logo: image });
          },
        },
        {
          name: 'favicon',
          required: false,
          type: 'image',
          label: 'Favicon',
          image: seller.favicon,
          previewSize: 'favicon',
          onUpdate: async image => {
            await updateSeller({ favicon: image });
          },
        },
        {
          name: 'brandColor',
          required: false,
          type: 'color-select',
          label: 'Brand color',
        },
        {
          name: 'showCheckoutPageBranding',
          type: 'toggle',
          label: 'Powered by Checkout Page branding',
          // Renders its own top + bottom rules with symmetric padding.
          dividers: true,
          locked: !canRemoveBranding,
          feature: Features.CHECKOUT_PAGE_BADGE,
          lockDescription:
            seller.subscriptionStatus === 'trialing'
              ? 'Checkout Page branding is shown during your trial. Upgrade to a paid plan to remove it.'
              : 'The Free plan always shows Checkout Page branding. Upgrade to a paid plan to remove it.',
        },
        {
          name: 'defaultCurrency',
          required: true,
          type: 'currency',
          label: 'Default currency',
        },
      ]}
      onSubmit={async values => {
        try {
          const { data } = await updateSeller(values);

          return {
            data,
          };
        } catch (error) {
          return {
            error,
          };
        }
      }}
    />
  );
};

export default StoreForm;
